import { useEffect, useLayoutEffect, useState } from "react";
import { ToastContainer } from "react-toastify";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import SearchIcon from "@mui/icons-material/Search";

import * as api from "../../../shared/lib/api";
import { useCore } from "../../../shared/hooks/use-core";
import { Paginator } from "../../../shared/components/paginator";
import UsersWebList, { UserWebListItem } from "./UsersWebList";

type ListUsersWebResponse = {
	content: {
		count: number;
		next: number | null;
		previous: number | null;
		results: UserWebListItem[];
	};
};

type PersonTypeFilter = "all" | "pf" | "pj";

export default function UsersWeb(): JSX.Element {
	const { setTitleBar, setPathTitleBar } = useCore();

	const [users, setUsers] = useState<UserWebListItem[]>([]);
	const [usersSearch, setUsersSearch] = useState<UserWebListItem[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [count, setCount] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [personType, setPersonType] = useState<PersonTypeFilter>("all");

	const buildListUrl = (page: number) =>
		`user/list_company_user/?profile=4&page=${page}`;

	const getUsers = async (url: string) => {
		const response = await api.get<ListUsersWebResponse>(url);
		setCount(response.content.count);
		setUsers(response.content.results);
		setUsersSearch(response.content.results);
		setLoading(false);
	};

	const handleChangePage = async (
		_event: React.ChangeEvent<unknown>,
		value: number,
	) => {
		setLoading(true);
		setPageNumber(value);
		await getUsers(buildListUrl(value));
	};

	const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		applyFilters(e.target.value, personType);
	};

	const handleChangePersonType = (e: SelectChangeEvent) => {
		const value = e.target.value as PersonTypeFilter;
		setPersonType(value);
		applyFilters(searchTerm, value);
	};

	function applyFilters(term: string, type: PersonTypeFilter) {
		const lower = term.toLowerCase();
		setUsersSearch(
			users.filter((item) => {
				const matchesTerm =
					item.first_name.toLowerCase().indexOf(lower) > -1 ||
					item.last_name.toLowerCase().indexOf(lower) > -1 ||
					item.email.toLowerCase().indexOf(lower) > -1 ||
					(item.cpf?.toLowerCase().indexOf(lower) ?? -1) > -1 ||
					(item.company?.cnpj?.toLowerCase().indexOf(lower) ?? -1) > -1;
				const matchesType =
					type === "all" ||
					(type === "pf" && Boolean(item.cpf)) ||
					(type === "pj" && Boolean(item.company));
				return matchesTerm && matchesType;
			}),
		);
	}

	useEffect(() => {
		getUsers(buildListUrl(1)).catch(console.error);
	}, []);

	useLayoutEffect(() => {
		setTitleBar("Usuários Web");
		setPathTitleBar("/new-user-web");
	}, [setPathTitleBar, setTitleBar]);

	return (
		<>
			<ToastContainer />
			<List dense disablePadding sx={{ width: "100%" }}>
				<ListItem
					disablePadding
					sx={{
						display: "flex",
						alignItems: "stretch",
						flexDirection: { xs: "column", sm: "row" },
						gap: { xs: 0, sm: 2 },
					}}
				>
					<FormControl sx={{ m: 1, flex: 1 }} variant="standard">
						<InputLabel htmlFor="input-search">Pesquisar</InputLabel>
						<Input
							id="input-search"
							placeholder="Nome, e-mail ou documento"
							startAdornment={
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							}
							onChange={handleChangeSearch}
						/>
					</FormControl>

					<FormControl
						sx={{ m: 1, minWidth: { xs: "auto", sm: 180 } }}
						variant="standard"
					>
						<InputLabel id="person-type-label">Tipo de documento</InputLabel>
						<Select
							labelId="person-type-label"
							id="person-type-filter"
							value={personType}
							onChange={handleChangePersonType}
						>
							<MenuItem value="all">Todos</MenuItem>
							<MenuItem value="pf">CPF - Pessoa Física</MenuItem>
							<MenuItem value="pj">CNPJ - Pessoa Jurídica</MenuItem>
						</Select>
					</FormControl>
				</ListItem>
			</List>
			<UsersWebList users={usersSearch} loading={loading} />
			<Paginator
				count={count}
				pageNumber={pageNumber}
				onChange={handleChangePage}
			/>
		</>
	);
}
